/**
 * HR sidebar — las etiquetas VISIBLES viven aquí, no en la tabla `menus`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LO QUE ESTE FICHERO ES, Y LO QUE NO
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `hasMenuAccess(ruta)` decide SI la entrada se ve. El texto de la entrada es
 * un literal de aquí. La tabla `menus` de la base **no aporta el rótulo**.
 *
 * Esa separación costó un despliegue entero: se renombró `MENU_DEFINITIONS` en
 * el backend, se desplegó, y la barra lateral siguió diciendo lo mismo — porque
 * el nombre nunca salió de ahí. R32 lo dice («*Also sweep `NavigationBar.js`,
 * `MobileMetroMenu.js`…*») y el barrido no se hizo.
 *
 * Desde ahora lo guarda `tests/test_navegacion_y_menus_coinciden.py`: falla si
 * una ruta de `MENU_DEFINITIONS` no tiene entrada aquí, y si una entrada de aquí
 * apunta a un menú que no existe.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * LAS SECCIONES SIGUEN AL FLUJO OBJETIVO
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Antes había «Company Personnel» y «Project Personnel» como dos secciones. Eso
 * es el mismo interruptor binario que la etapa 4a quitó de la base, pintado en
 * el menú: la división Company/Project es **centro de coste**, no dos mundos.
 *
 * Ahora: Recruitment · Personnel · Timesheet · Payroll · Reports · Configuration.
 *
 * Payroll conserva sus dos entradas a propósito: no hay pantalla de nómina
 * unificada todavía, y fundirlas sin ella quitaría el acceso a una de las dos.
 */

/**
 * Las rutas de menú que la sección HR reclama.
 *
 * Tiene que coincidir con las rutas `/hr/*` de `MENU_DEFINITIONS`
 * (`backend/sync_menus.py`). El guard lo exige en las dos direcciones.
 */
export const HR_CHILD_ROUTES = [
  '/hr/candidates',
  '/hr/candidates/new',
  '/hr/blacklist',
  '/hr/hse-induction',
  '/hr/personnel',
  '/hr/company/available',
  '/hr/key-personnel',
  '/hr/timesheet/month',
  '/hr/timesheet/entries',
  '/hr/payroll/company',
  '/hr/payroll/project',
  '/hr/reports/assignments',
  '/hr/reports/statistics',
  '/hr/timesheet/reports',
  '/hr/timesheet/codes',
  '/hr/configuration',
];

/**
 * HR sidebar flyout sections — aligned with mobile metro subsections.
 */
export function buildHrFlyoutSections(hasMenuAccess, { activeProject } = {}) {
  const sections = [];

  const recruitment = [
    hasMenuAccess('/hr/candidates') ? { label: 'Recruitment', path: '/hr/candidates' } : null,
    hasMenuAccess('/hr/candidates/new') ? { label: '+ New Candidate', path: '/hr/candidates/new' } : null,
    hasMenuAccess('/hr/blacklist') ? { label: 'Blacklist', path: '/hr/blacklist' } : null,
    hasMenuAccess('/hr/hse-induction') ? { label: 'HSE Induction', path: '/hr/hse-induction' } : null,
  ].filter(Boolean);
  if (recruitment.length > 0) {
    sections.push({
      id: 'recruitment',
      label: 'Recruitment',
      flyoutTitle: 'Recruitment',
      items: recruitment,
    });
  }

  // Contratados y ex-trabajadores, en una sola entrada. El filtro de liquidados
  // vive DENTRO de la pantalla, no en el menu: dos entradas para el mismo
  // registro es la division que estamos quitando.
  const personnel = [
    hasMenuAccess('/hr/personnel') ? { label: 'Personnel', path: '/hr/personnel' } : null,
    hasMenuAccess('/hr/company/available')
      ? { label: 'Available for Projects', path: '/hr/company/available' }
      : null,
    hasMenuAccess('/hr/key-personnel')
      ? { label: 'Key Personnel CVs', path: '/hr/key-personnel' }
      : null,
  ].filter(Boolean);
  if (personnel.length > 0) {
    sections.push({
      id: 'personnel',
      label: 'Personnel',
      flyoutTitle: 'Personnel',
      items: personnel,
    });
  }

  const timesheet = [
    hasMenuAccess('/hr/timesheet/month') ? { label: 'Timesheet', path: '/hr/timesheet/month' } : null,
    hasMenuAccess('/hr/timesheet/entries')
      ? { label: 'Timesheet Entries', path: '/hr/timesheet/entries' }
      : null,
  ].filter(Boolean);
  if (timesheet.length > 0) {
    sections.push({
      id: 'timesheet',
      label: 'Timesheet',
      flyoutTitle: 'Timesheet',
      items: timesheet,
    });
  }

  // Las dos nominas siguen separadas: la pantalla unificada es la segunda parte
  // de la etapa 5, y fundir los menus sin ella quitaria el acceso a una.
  const payroll = [
    hasMenuAccess('/hr/payroll/company')
      ? { label: 'Corporate Payroll', path: '/hr/payroll/company' }
      : null,
    activeProject && hasMenuAccess('/hr/payroll/project')
      ? { label: 'Project Payroll', path: '/hr/payroll/project' }
      : null,
  ].filter(Boolean);
  if (payroll.length > 0) {
    sections.push({
      id: 'payroll',
      label: 'Payroll',
      flyoutTitle: 'Payroll',
      items: payroll,
    });
  }

  const reports = [
    hasMenuAccess('/hr/reports/assignments')
      ? { label: 'Assignment History', path: '/hr/reports/assignments' }
      : null,
    hasMenuAccess('/hr/reports/statistics')
      ? { label: 'Workforce Statistics', path: '/hr/reports/statistics' }
      : null,
    hasMenuAccess('/hr/timesheet/reports')
      ? { label: 'Monthly Reports', path: '/hr/timesheet/reports' }
      : null,
  ].filter(Boolean);
  if (reports.length > 0) {
    sections.push({
      id: 'reports',
      label: 'Reports & Analytics',
      flyoutTitle: 'Reports & Analytics',
      items: reports,
    });
  }

  const config = [
    hasMenuAccess('/hr/timesheet/codes')
      ? { label: 'Timesheet Codes', path: '/hr/timesheet/codes' }
      : null,
    hasMenuAccess('/hr/configuration')
      ? { label: 'HR Settings', path: '/hr/configuration' }
      : null,
  ].filter(Boolean);
  if (config.length > 0) {
    sections.push({
      id: 'config',
      label: 'Configuration',
      flyoutTitle: 'Configuration',
      items: config,
    });
  }

  return sections;
}
